<template>
  <div class="bonus-table-container">
    <div class="bonus-table-header">
      <div class="date-select">
        <div v-if="showOptions" class="date-options">
          <button v-for="(item, idx) in dateOptions" :key="item.value"
            :class="['option-btn', { active: idx === selectedDate }]" @click="selectDate(idx)">
            {{ t(`pendente.${item.labelKey}`) }}
          </button>
        </div>
      </div>
      <div class="bonus-value">
        {{ t('pendente.bonus') }} <span class="yellow">{{ numberFormat(totalAmount) }}</span>
      </div>
    </div>
    <div class="bonus-table">
      <div class="table-header">
        <div class="th">{{ t('pendente.time') }}</div>
        <div class="th">{{ t('pendente.eventName') }}</div>
        <div class="th">{{ t('pendente.eventReward') }}</div>
        <div class="th">{{ t('pendente.source') }}</div>
      </div>
      <div v-if="tableData.length > 0">
        <div v-for="(row, idx) in tableData" :key="idx" :class="['table-row', { zebra: idx % 2 === 1 }]">
          <div class="td">{{ formatTime(row.time) }}</div>
          <div class="td">{{ row.names || '--' }}</div>
          <div class="td color-[#D3D53B]">{{ row.amount }}</div>
          <div class="td">{{ getTypeText(row.type) }}</div>
        </div>
      </div>
      <div v-else>
        <div class="empty-data">
          <div class="flex justify-center items-center">
            <img :src="getAssetsFile('home/No_record@2x.png')" alt=""  class="max-h-[84px] max-w-[106.5px] w-auto h-auto">
          </div>
          <div class="empty-text">{{ t('pendente.noRecords') }}</div>
        </div>
      </div>
    </div>
    <div class="bonus-table-footer-container">
      <div class="bonus-table-footer">
        {{ t('pendente.collectAll') }}
      </div>
    </div>
  </div>

  <!-- 登录弹窗 -->
  <AuthDialog :show="showAuthDialog" :is-login="true" @close="handleAuthDialogClose" />
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import * as GameApi from '@/api/game'
import dayjs from 'dayjs'
import { numberFormat } from '@/utils/formatter'
import { getAssetsFile } from '@/utils/pub-use'
import { useUserStore } from '@/stores/modules/user'
import AuthDialog from '@/components/AuthDialog.vue'
import { formatTime } from '@/utils/date'

const { t } = useI18n()
const router = useRouter()
const userStore = useUserStore()

// 登录弹窗状态
const showAuthDialog = ref(false)

// 获取当天0点
function getStartOfDay(date = new Date()) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0)
}

// 格式化为 yyyy-MM-dd 00:00:00
function formatDate(date: Date) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d} 00:00:00`
}

const now = new Date()
const today = getStartOfDay(now)
const yesterday = getStartOfDay(new Date(now.getTime() - 24 * 60 * 60 * 1000))
const last7 = getStartOfDay(new Date(now.getTime() - 6 * 24 * 60 * 60 * 1000)) // 近7天含今天
const last30 = getStartOfDay(new Date(now.getTime() - 29 * 24 * 60 * 60 * 1000)) // 近30天含今天

const dateOptions = [
  { labelKey: 'today', value: formatDate(today) },
  { labelKey: 'yesterday', value: formatDate(yesterday) },
  { labelKey: 'last7Days', value: formatDate(last7) },
  { labelKey: 'last30Days', value: formatDate(last30) }
]

const showOptions = ref(false)
const selectedDate = ref(0)
const tableData = ref([])
const pageNo = ref(1)
const pageSize = ref(10) // 这里不用手动改，fetchData里会自动赋值
const total = ref(0)     // 总条数

const totalAmount = computed(() => {
  return tableData.value.reduce((sum, row) => {
    const n = Number(row.amount)
    return sum + (isNaN(n) ? 0 : n)
  }, 0)
})

// 检查登录状态
function checkLoginStatus() {
  if (!userStore.isLoggedIn) {
    showAuthDialog.value = true
    return false
  }
  return true
}

// 处理登录弹窗关闭
function handleAuthDialogClose() {
  showAuthDialog.value = false
  // 如果用户关闭登录弹窗，跳转回eventos标签页
  router.replace({
    path: '/activity',
    query: { tab: 'eventos' }
  })
}

function selectDate(idx: number) {
  selectedDate.value = idx
  showOptions.value = false
  pageNo.value = 1 // 切换时间时重置到第一页
  fetchData()
}

// 请求参数
function fetchData() {
  // 动态设置 pageSize
  let size = 10
  if (selectedDate.value === 1) size = 20
  else if (selectedDate.value === 2) size = 50
  else if (selectedDate.value === 3) size = 100
  pageSize.value = size

  const parmes = {
    time: dateOptions[selectedDate.value].value,
    type: 3,
    pageNo: pageNo.value,
    pageSize: pageSize.value
  }
  GameApi.accountFlow(parmes).then(res => {
    tableData.value = res.data.assetRecords.list
    total.value = res.data.total || 0
  })
}

// 分页切换
function handlePageChange(newPage: number) {
  if (newPage < 1 || newPage > totalPages.value) return
  pageNo.value = newPage
  fetchData()
}

const totalPages = computed(() => Math.ceil(total.value / pageSize.value))

const typeMap: Record<number, string> = {
  1: t('pendente.betting'),
  2: t('pendente.reward'),
  3: t('pendente.recharge'),
  4: t('pendente.rechargeNoOrder'),
  5: t('pendente.withdraw'),
  6: t('pendente.withdrawNoOrder'),
  7: t('pendente.promoterRanking'),
  8: t('pendente.promoterCommission'),
  9: t('pendente.payment'),
  10: t('pendente.refund'),
  11: t('pendente.deposit'),
  12: t('pendente.withdrawRequest'),
  13: t('pendente.withdrawApproved'),
  14: t('pendente.withdrawRejected'),
  15: t('pendente.eftPayment'),
  16: t('pendente.eftReturn'),
  17: t('pendente.giftCodeReward'),
  18: t('pendente.firstRechargeReward'),
  19: t('pendente.repeatRechargeReward'),
  20: t('pendente.bankruptcyActivity'),
  21: t('pendente.checkinActivity'),
  22: t('pendente.redPacketRain'),
  23: t('pendente.customerLossActivity'),
  24: t('pendente.manualAdjustment'),
  25: t('pendente.shareActivity'),
  26: t('pendente.vipLevelReward'),
  27: t('pendente.redPacketActivity'),
  28: t('pendente.vipMonthlyReward'),
  29: t('pendente.promoterTreasure'),
  30: t('pendente.luckyWheel'),
  31: t('pendente.bindEmailReward'),
  32: t('pendente.bankRefundAfterWithdraw'),
  33: t('pendente.bettingWithReward')
}

function getTypeText(type: number) {
  return typeMap[type] || type
}

onMounted(() => {
  // 检查登录状态
  if (!checkLoginStatus()) {
    return
  }

  fetchData()
})

// 监听登录状态变化
watch(() => userStore.isLoggedIn, (isLoggedIn) => {
  if (isLoggedIn && showAuthDialog.value) {
    // 用户登录成功，关闭弹窗并加载数据
    showAuthDialog.value = false
    fetchData()
  }
})
</script>

<style scoped>
.bonus-table-container {
  border-radius: 8px;
  padding: 18px 18px 0 18px;
  min-height: 300px;
}

.bonus-table-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
}

.date-select {
  position: relative;
}

.date-btn {
  background: transparent;
  border: 1px solid #40b665;
  color: #fff;
  border-radius: 8px;
  padding: 4px 18px 4px 12px;
  font-size: 16px;
  font-weight: 500;
  outline: none;
  cursor: pointer;
  position: relative;
  display: flex;
  align-items: center;
}

.arrow {
  display: inline-block;
  margin-left: 6px;
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 6px solid #40b665;
  transition: transform 0.2s;
}

.arrow.up {
  transform: rotate(180deg);
}

.arrow.down {
  transform: rotate(0deg);
}

.date-options {
  position: absolute;
  top: 38px;
  left: 0;
  background: #02523d;
  border-radius: 10px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.18);
  display: flex;
  z-index: 10;
  padding: 10px 8px;
  gap: 8px;
  width: max-content;
  min-width: unset;
  max-width: 100vw;
}

.option-btn {
  background: transparent;
  border: 1px solid #40b665;
  color: #40b665;
  border-radius: 8px;
  padding: 8px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
  min-width: 60px;
  text-align: center;
  white-space: nowrap;
}

.option-btn.active,
.option-btn:hover {
  background: linear-gradient(90deg, #40b665 0%, #1ecb7b 100%);
  color: #fff;
  border: 1px solid #40b665;
}

.bonus-value {
  color: #fff;
  font-size: 13px;
  font-weight: 500;
}

.bonus-value .yellow {
  color: #FEF64A;
}

.bonus-table {
  margin-top: 8px;
}

.table-header {
  display: flex;
  color: #fff;
  font-size: 12px;
  font-weight: 500;
  gap: 0;
}

.th {
  flex: 1;
  text-align: center;
  padding: 8px 0;
}

.table-row {
  display: flex;
  font-size: 12px;
  color: #fff;
  align-items: center;
  min-height: 36px;
}

.table-row .td {
  flex: 1;
  text-align: center;
  padding: 8px 0;
}

.table-row.zebra {
  background: rgba(64, 182, 101, 0.08);
}

.empty-data {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-top: 115px;
}

.empty-text {
  color: #ADB1B0;
  font-size: 11px;
  margin-top: 21px;
}

.bonus-table-footer-container {
  position: fixed;
  bottom: 90px;
  width: 100%;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.bonus-table-footer {
  width: 169px;
  height: 39px;
  background: linear-gradient(0deg, #796F22 0%, #7C7A36 100%);
  box-shadow: 1px 1px 1px 0px #0B291E;
  border-radius: 5px;
  color: #ABA778;
  font-size: 13px;
  text-align: center;
  line-height: 39px;
}
</style>
